/**
 * Mode sub-nav context — the seam that lifts a mode's views out of the page
 * (the old top-of-page `PageNav` tab strip) and up into the left ModeRail, where
 * they nest under the active mode and animate open.
 *
 * It mirrors `rail-extension-context.tsx` exactly: a page deep in `children`
 * pushes its views via `useProvideModeSubNav` (through the tiny
 * `ModeSubNavRegistrar`, so a server component like `ModeShell` can register
 * without becoming a client component), and the layout-level ModeRail reads them
 * with `useModeSubNav`. The provider wraps the whole content+rail row so both
 * sides share one signal. Only ONE page is mounted at a time, so the registered
 * items always belong to the currently-active mode — but during a navigation the
 * URL flips to the incoming mode a frame before the outgoing registrar unmounts,
 * so each registration is tagged with its OWNER mode (see ModeSubNavState) and
 * the rail ignores a registration whose owner ≠ the active mode.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/** One sub-nav item under the active mode (a mode view, e.g. List · Board). */
export interface ModeSubNavItem {
  href: string;
  label: string;
  /** Pre-rendered icon JSX (the lucide ref never crosses server→client raw). */
  icon?: ReactNode;
  /** Override the path used for active matching (defaults to href). */
  activeHref?: string;
  /** The section's pulse area slug — the EXACT join key for this view's trailing
   *  health dot. Set it whenever the route tail diverges from the pulse area
   *  (route-owning domains like Agents ▸ Workflows → `/agents/workflows`, whose
   *  pulse area is `fleet`); the rail falls back to deriving the area from the
   *  href when absent (`?area=` URLs join fine on their own). */
  area?: string;
}

/**
 * The current registration: a mode's sub-nav items + WHICH mode owns them.
 * The owner slug lets the rail reject a stale cross-mode registration during the
 * navigation window described in the module header.
 */
export interface ModeSubNavState {
  items: ModeSubNavItem[] | null;
  /** Mode slug these items belong to (e.g. "customers"), or null when cleared. */
  mode: string | null;
}

const EMPTY_STATE: ModeSubNavState = { items: null, mode: null };

interface Ctx {
  state: ModeSubNavState;
  setState: (next: ModeSubNavState) => void;
}

const ModeSubNavContext = createContext<Ctx | null>(null);

export function ModeSubNavProvider({ children }: { children: ReactNode }) {
  const [state, setStateRaw] = useState<ModeSubNavState>(EMPTY_STATE);
  // Guard against callers that hand us a fresh array identity every render
  // (ModeShell maps `views` inline) — comparing by the visible fields breaks the
  // set→render→set loop, exactly as RailExtensionProvider does.
  const setState = useCallback((next: ModeSubNavState) => {
    setStateRaw((prev) => (sameState(prev, next) ? prev : next));
  }, []);
  const value = useMemo(() => ({ state, setState }), [state, setState]);
  return (
    <ModeSubNavContext.Provider value={value}>
      {children}
    </ModeSubNavContext.Provider>
  );
}

function sameState(a: ModeSubNavState, b: ModeSubNavState): boolean {
  return a.mode === b.mode && sameItems(a.items, b.items);
}

function sameItems(
  a: ModeSubNavItem[] | null,
  b: ModeSubNavItem[] | null,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  return a.every((x, i) => {
    const y = b[i]!;
    return (
      x.href === y.href &&
      x.label === y.label &&
      x.activeHref === y.activeHref &&
      // Compare `area` too so the dedup guard stays honest if a route ever varies
      // the health-dot join key for an otherwise-identical row. Adding a field can
      // only make the comparator detect MORE changes (never spuriously loop), since
      // a stable registration still matches on every field.
      x.area === y.area
    );
  });
}

export function useModeSubNav(): ModeSubNavState {
  const ctx = useContext(ModeSubNavContext);
  return ctx?.state ?? EMPTY_STATE;
}

/** Push mode sub-nav items (tagged with their owner mode) while this component
 *  is mounted; clears on unmount. */
export function useProvideModeSubNav(
  items: ModeSubNavItem[] | null,
  mode: string | null,
): void {
  const ctx = useContext(ModeSubNavContext);
  const set = ctx?.setState;

  const stableSet = useCallback(
    (next: ModeSubNavState) => {
      set?.(next);
    },
    [set],
  );

  useEffect(() => {
    stableSet({ items, mode });
    return () => stableSet(EMPTY_STATE);
  }, [stableSet, items, mode]);
}

/** A render-nothing client child that registers mode sub-nav items. Lets a
 *  server component (ModeShell) lift its views into the rail without itself
 *  becoming a client component. `mode` is the owner slug (see ModeSubNavState). */
export function ModeSubNavRegistrar({
  items,
  mode,
}: {
  items: ModeSubNavItem[] | null;
  mode?: string | null;
}) {
  useProvideModeSubNav(items, mode ?? null);
  return null;
}
